import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleButton, withStyles, MaterialSymbol } from 'vopay';
import { useToast } from '#shared/providers/Toast';
import styles from './CopyButton.module.scss';

const cn = withStyles(styles);

export type CopyButtonProps = {
  content: string | number;
  /**
   * `icon` (default): the bare copy glyph the shared tables use. `circle`: the round action button the
   * portals' credential strips use, beside their other round buttons.
   */
  variant?: 'icon' | 'circle';
  /** `icon` only. */
  color?: CSSProperties['color'];
  /** `icon` only. */
  size?: CSSProperties['fontSize'];
  /** Names what is copied, for a screen reader and for telling copy buttons apart. Default "Copy". */
  'aria-label'?: string;
  disabled?: boolean;
};

const PULSE_DURATION_MS = 200;

/** Copies `content` to the clipboard and confirms it with the standard toast. Every app's copy button. */
export default function CopyButton({
  content,
  variant = 'icon',
  color = 'hsla(216, 100%, 59%, 1)',
  size = '24px',
  'aria-label': ariaLabel,
  disabled = false,
}: CopyButtonProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared' });
  const toast = useToast();
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (animating) {
      const timeout = setTimeout(() => {
        setAnimating(false);
      }, PULSE_DURATION_MS);

      return () => clearTimeout(timeout);
    }

    return () => {};
  }, [animating]);

  const label = ariaLabel ?? t('Buttons.copy', { defaultValue: 'Copy' });

  // The browser can refuse the write (no permission, no focus), so the confirmation waits for it.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content.toString());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.neutral(
        t('copyError', { defaultValue: 'Could not copy to the clipboard: {{errorMessage}}', errorMessage }),
      );

      return;
    }

    toast.success(t('copied', { defaultValue: 'Copied to clipboard' }));
  };

  if (variant === 'circle') {
    return (
      <CircleButton icon="content_copy" colorScheme="action" aria-label={label} disabled={disabled} onClick={copy} />
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      style={{
        '--duration': `${PULSE_DURATION_MS}ms`,
      }}
      className={cn('copyButton', animating && 'animating')}
      onClick={() => {
        setAnimating(true);
        copy();
      }}
    >
      <MaterialSymbol icon="content_copy" size={size} color={color} className={cn('icon')} />
    </button>
  );
}
